import { FormFields, Priorities, LinearLabels } from "../constants";

const SUCCESS_CODE = 201;

export interface LabelsMap {
  [key: string]: string;
}

export interface SubmitIssueParams
  extends Omit<FormFields, "userDescription" | "attachments" | "reporter"> {
  teamId: string;
  description: string;
  labels: string[];
  token: string;
}

/**
 * Submits an issue to Linear
 * @param params.teamId the teamId of the Linear Team to create an issue under
 * @param params.title the title of the issue
 * @param params.description a summary of the issue
 * @param params.reporter whoever reported the issue
 * @param params.priority the priority of the issue
 * @returns whether or not issue submission was successful
 */
export const submitIssue = async ({
  teamId,
  title,
  description,
  priority,
  labels,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  token, // TODO: used for hackerapi auth
}: SubmitIssueParams): Promise<boolean> => {
  const query = `
  mutation IssueCreate($input: IssueCreateInput!) {
    issueCreate(input: $input) {
      success
      issue {
        id
        title
      }
    }
  }
`;

  const variables = {
    input: {
      title,
      description,
      teamId,
      priority: Object.values(Priorities).indexOf(priority),
      labelIds: labels,
    },
  };

  const response = await fetch("https://api.linear.app/graphql", {
    method: "POST",
    headers: {
      Authorization: `${process.env.REACT_APP_LINEAR_API}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ query, variables }),
  });

  return response.status === SUCCESS_CODE;
};

export const fetchLabels = async (teamId: string): Promise<string[]> => {
  const query = `
    query LabelsByTeam($teamId: ID!) {
      issueLabels(filter: { team: { id: { eq: $teamId } } }) {
        nodes {
          id
          name
        }
      }
    }
  `;

  const variables = {
    teamId: teamId,
  };

  const response = await fetch("https://api.linear.app/graphql", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `${process.env.REACT_APP_LINEAR_API}`,
    },
    body: JSON.stringify({ query, variables }),
  });

  const result = [];
  const data = JSON.parse(await response.text());

  if (response.ok) {
    const labels = data?.data?.issueLabels?.nodes || [];

    for (let i = 0; i < labels.length; i++) {
      if (LinearLabels.includes(labels[i].name)) {
        result.push(labels[i].id);
      }
    }
  } else {
    console.error("Linear labels could not be fetched");
  }

  return result;
};
